/**
 * YouTube Visualizer
 *
 * Native chat renderer for YouTube how-to video markers.
 *
 * Primary responsibilities:
 * - Play a selected YouTube video inside the message
 * - Show multi-video recommendations in a horizontal scroller
 *
 * Dependencies:
 * - YouTube marker parser types
 * - Tabler icons
 */

/* eslint-disable tailwindcss/classnames-order */

'use client'

import { useMemo, useState } from 'react'
import Image from 'next/image'
import { IconBrandYoutube, IconExternalLink, IconPlayerPlay } from '@tabler/icons-react'

import { cn } from '@/lib/utils'
import type { YoutubeVideoMarker } from '@/lib/youtube-markers'

type YouTubeVisualizerProps = {
  title?: string
  videos: YoutubeVideoMarker[]
}

// --------------

const getVideoLabel = (video: YoutubeVideoMarker) =>
  video.channel ? `${video.title} by ${video.channel}` : video.title

// --------------

export function YouTubeVisualizer({ title, videos }: YouTubeVisualizerProps) {
  const validVideos = useMemo(() => videos.filter((video) => video.id && video.embedUrl), [videos])
  const [selectedId, setSelectedId] = useState(validVideos[0]?.id ?? '')
  const selectedVideo = validVideos.find((video) => video.id === selectedId) ?? validVideos[0]

  if (!selectedVideo) return null

  const hasMultipleVideos = validVideos.length > 1

  return (
    <section
      aria-label={title || 'YouTube videos'}
      className="not-prose my-4 w-full max-w-3xl overflow-hidden rounded-lg border border-border/70 bg-background shadow-sm"
      data-youtube-visualizer="true"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/70 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-red-500/10 text-red-600 dark:text-red-300">
            <IconBrandYoutube aria-hidden="true" size={18} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {title || 'YouTube how-to videos'}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {hasMultipleVideos
                ? `${validVideos.length} playable recommendations`
                : getVideoLabel(selectedVideo)}
            </p>
          </div>
        </div>
        <a
          aria-label={`Open ${selectedVideo.title} on YouTube`}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          href={selectedVideo.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <IconExternalLink aria-hidden="true" size={17} />
        </a>
      </div>

      <div className="bg-black">
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="aspect-video w-full"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          src={selectedVideo.embedUrl}
          title={selectedVideo.title}
        />
      </div>

      {(selectedVideo.summary || selectedVideo.channel || selectedVideo.duration) && (
        <div className="space-y-1 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-foreground">{selectedVideo.title}</p>
            {selectedVideo.duration && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {selectedVideo.duration}
              </span>
            )}
          </div>
          {selectedVideo.channel && (
            <p className="text-xs text-muted-foreground">{selectedVideo.channel}</p>
          )}
          {selectedVideo.summary && (
            <p className="text-sm leading-relaxed text-muted-foreground">{selectedVideo.summary}</p>
          )}
        </div>
      )}

      {hasMultipleVideos && (
        <div
          aria-label="YouTube video recommendations"
          className="flex gap-2 overflow-x-auto border-t border-border/70 p-3"
        >
          {validVideos.map((video) => {
            const isSelected = video.id === selectedVideo.id

            return (
              <button
                aria-pressed={isSelected}
                className={cn(
                  'group relative flex w-52 shrink-0 flex-col overflow-hidden rounded-lg border border-border/70 bg-card text-left text-card-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  isSelected ? 'border-red-500/70 shadow-sm' : 'hover:border-foreground/30'
                )}
                key={video.id}
                onClick={() => setSelectedId(video.id)}
                type="button"
              >
                <span className="relative block aspect-video overflow-hidden bg-black">
                  <Image
                    alt=""
                    className="size-full object-cover transition-transform group-hover:scale-[1.02]"
                    height={180}
                    loading="lazy"
                    src={video.thumbnailUrl}
                    width={320}
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/10">
                    <span className="flex size-9 items-center justify-center rounded-full bg-black/70 text-white">
                      <IconPlayerPlay aria-hidden="true" size={18} />
                    </span>
                  </span>
                  {video.duration && (
                    <span className="absolute bottom-1.5 right-1.5 rounded bg-black/80 px-1.5 py-0.5 text-[11px] font-medium text-white">
                      {video.duration}
                    </span>
                  )}
                </span>
                <span className="min-h-[72px] space-y-1 p-2">
                  <span className="line-clamp-2 text-xs font-semibold leading-snug text-foreground">
                    {video.title}
                  </span>
                  {video.channel && (
                    <span className="line-clamp-1 text-[11px] text-muted-foreground">
                      {video.channel}
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
